import { useState, useEffect, useMemo, useCallback } from "react";
import { API } from "@/App";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { 
  Calendar as CalendarIcon, 
  BarChart3, 
  MousePointerClick,
  Eye,
  Percent,
  Globe,
  ExternalLink,
  ChevronLeft,
  ChevronRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Info,
  Link2Off,
  Mail,
  MailOpen,
  MousePointer
} from "lucide-react";
import { format, subDays, parseISO } from "date-fns";
import Sidebar from "@/components/Sidebar";

// Helper component for N/A tooltip
const NAValue = ({ reason }) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="text-gray-500 cursor-help flex items-center gap-1">
          N/A
          <Info className="w-3 h-3" />
        </span>
      </TooltipTrigger>
      <TooltipContent className="bg-[#1a1a24] border-white/10 text-white max-w-xs">
        <p>{reason}</p>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

export default function Analytics() {
  const getRateColor = (rate, highThreshold) => {
    if (rate >= highThreshold) return "text-emerald-400";
    if (rate > 0) return "text-amber-400";
    return "text-gray-500";
  };

  const navigate = useNavigate();
  const [lotteries, setLotteries] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLottery, setSelectedLottery] = useState("all");
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 7),
    to: new Date(),
  });
  const [presetRange, setPresetRange] = useState("7d");

  // Analytics data
  const [summary, setSummary] = useState({ 
    total_impressions: 0, 
    total_clicks: 0, 
    ctr: 0, 
    total_banners: 0,
    has_clickable_banners: false,
    clickable_banner_count: 0,
    display_only_count: 0
  });
  const [bannerData, setBannerData] = useState({ data: [], pagination: {} });
  const [overTime, setOverTime] = useState([]);
  const [byDomain, setByDomain] = useState([]);
  const [emailStats, setEmailStats] = useState({ emails_sent: 0, emails_opened: 0, emails_clicked: 0, open_rate: 0, click_rate: 0 });
  const [emailOverTime, setEmailOverTime] = useState([]);
  const [topBannersEmail, setTopBannersEmail] = useState([]);

  // Table pagination and sorting
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize] = useState(10);
  const [sortBy, setSortBy] = useState("impressions");
  const [sortOrder, setSortOrder] = useState("desc");

  const loadInitialData = useCallback(async () => {
    try {
      const [lotteriesRes, brandsRes] = await Promise.all([
        axios.get(`${API}/lotteries`),
        axios.get(`${API}/brands`).catch(() => ({ data: [] })),
      ]);
      setLotteries(lotteriesRes.data);
      setBrands(brandsRes.data);
      setLoading(false);
    } catch (error) {
      toast.error("Failed to load data");
      setLoading(false);
    }
  }, []);

  const loadAnalytics = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append("start_date", format(dateRange.from, "yyyy-MM-dd"));
      if (dateRange.to) params.append("end_date", format(dateRange.to, "yyyy-MM-dd"));
      if (selectedLottery !== "all") params.append("lottery_id", selectedLottery);
      if (selectedBrand !== "all") params.append("brand_id", selectedBrand);
      
      const queryString = params.toString();
      
      const [summaryRes, overTimeRes, byDomainRes, emailStatsRes, emailOverTimeRes, topBannersRes] = await Promise.all([
        axios.get(`${API}/analytics/summary?${queryString}`),
        axios.get(`${API}/analytics/over-time?${queryString}`),
        axios.get(`${API}/analytics/by-domain?${queryString}`),
        axios.get(`${API}/analytics/email-stats?${queryString}`),
        axios.get(`${API}/analytics/email-over-time?${queryString}`),
        axios.get(`${API}/analytics/top-banners-email?${queryString}`),
      ]);

      setSummary(summaryRes.data);
      setOverTime(overTimeRes.data);
      setByDomain(byDomainRes.data);
      setEmailStats(emailStatsRes.data);
      setEmailOverTime(emailOverTimeRes.data);
      setTopBannersEmail(topBannersRes.data);
    } catch (error) {
      toast.error("Failed to load analytics data");
    }
  }, [dateRange, selectedLottery, selectedBrand]);

  const loadBannerData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (dateRange.from) params.append("start_date", format(dateRange.from, "yyyy-MM-dd"));
      if (dateRange.to) params.append("end_date", format(dateRange.to, "yyyy-MM-dd"));
      if (selectedLottery !== "all") params.append("lottery_id", selectedLottery);
      if (selectedBrand !== "all") params.append("brand_id", selectedBrand);
      params.append("sort_by", sortBy);
      params.append("sort_order", sortOrder);
      params.append("page", tablePage.toString());
      params.append("page_size", tablePageSize.toString());
      
      const res = await axios.get(`${API}/analytics/by-banner?${params.toString()}`);
      setBannerData(res.data);
    } catch (error) {
      toast.error("Failed to load banner data");
    }
  }, [dateRange, selectedLottery, selectedBrand, tablePage, tablePageSize, sortBy, sortOrder]);

  const setPreset = (preset) => {
    setPresetRange(preset);
    setTablePage(1); // Reset pagination on date change
    const today = new Date();
    switch (preset) {
      case "7d":
        setDateRange({ from: subDays(today, 7), to: today });
        break;
      case "30d":
        setDateRange({ from: subDays(today, 30), to: today });
        break;
      case "90d":
        setDateRange({ from: subDays(today, 90), to: today });
        break;
      case "all":
        setDateRange({ from: null, to: null });
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  useEffect(() => {
    if (!loading) {
      loadAnalytics();
    }
  }, [loading, loadAnalytics]);

  useEffect(() => {
    if (!loading) {
      loadBannerData();
    }
  }, [loading, loadBannerData]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setTablePage(1);
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return <ArrowUpDown className="w-4 h-4 ml-1 opacity-50" />;
    return sortOrder === "desc" ? 
      <ArrowDown className="w-4 h-4 ml-1 text-[#10B981]" /> : 
      <ArrowUp className="w-4 h-4 ml-1 text-[#10B981]" />;
  };

  // Get lottery name helper
  const getLotteryName = (lotteryId) => {
    const lottery = lotteries.find(l => l.id === lotteryId);
    return lottery?.name || "Unknown";
  };

  // Calculate chart max for scaling
  const maxImpressions = useMemo(() => {
    return Math.max(...overTime.map(d => d.impressions), 1);
  }, [overTime]);

  // Check if we have any clicks data
  const hasClicksData = useMemo(() => {
    return summary.has_clickable_banners && overTime.some(d => d.clicks > 0);
  }, [summary.has_clickable_banners, overTime]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] flex">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#10B981]"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0f] flex text-white">
      <Sidebar />
      <main className="flex-1 overflow-auto">
        <div className="p-8 max-w-7xl">
          {/* Header */}
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold">Analytics</h1>
              <p className="text-gray-400">Track banner impressions and engagement</p>
            </div>

            {/* Date Range Controls */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex bg-[#12121a] rounded-lg p-1">
                {[
                  { key: "7d", label: "7 days" },
                  { key: "30d", label: "30 days" },
                  { key: "90d", label: "90 days" },
                  { key: "all", label: "All Time" },
                ].map((preset) => (
                  <Button
                  key={preset.key}
                  variant="ghost"
                  size="sm"
                  className={`px-3 ${presetRange === preset.key ? "bg-[#10B981] text-white" : "text-gray-400 hover:text-white"}`}
                  onClick={() => setPreset(preset.key)}
                  data-testid={`preset-${preset.key}`}
                >
                  {preset.label}
                </Button>
              ))}
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="border-white/10 text-gray-400 hover:text-white" data-testid="custom-date-button">
                  <CalendarIcon className="w-4 h-4 mr-2" />
                  Custom
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-[#12121a] border-white/10" align="end">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => {
                    if (range) {
                      setDateRange(range);
                      setPresetRange("custom");
                      setTablePage(1);
                    }
                  }}
                  numberOfMonths={2}
                  className="bg-[#12121a]"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        {/* Lottery Filter */}
        <div className="flex items-center gap-4 mb-6">
          <span className="text-sm text-gray-500">Filter by lottery:</span>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant={selectedLottery === "all" ? "default" : "outline"}
              className={selectedLottery === "all" 
                ? "bg-[#10B981] hover:bg-[#059669]" 
                : "border-white/10 text-gray-400 hover:text-white hover:bg-white/5"}
              onClick={() => { setSelectedLottery("all"); setTablePage(1); }}
              data-testid="lottery-filter-all"
            >
              All Lotteries
            </Button>
            {lotteries.map(lottery => (
              <Button
                key={lottery.id}
                size="sm"
                variant={selectedLottery === lottery.id ? "default" : "outline"}
                className={selectedLottery === lottery.id 
                  ? "bg-[#10B981] hover:bg-[#059669]" 
                  : "border-white/10 text-gray-400 hover:text-white hover:bg-white/5"}
                onClick={() => { setSelectedLottery(lottery.id); setTablePage(1); }}
                data-testid={`lottery-filter-${lottery.id}`}
              >
                {lottery.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Brand Filter */}
        <div className="flex items-center gap-4 mb-8">
          <span className="text-sm text-gray-500">Filter by brand:</span>
          <div className="flex gap-2 flex-wrap">
            <Button
              size="sm"
              variant={selectedBrand === "all" ? "default" : "outline"}
              className={selectedBrand === "all" 
                ? "bg-[#10B981] hover:bg-[#059669]" 
                : "border-white/10 text-gray-400 hover:text-white hover:bg-white/5"}
              onClick={() => { setSelectedBrand("all"); setTablePage(1); }}
              data-testid="brand-filter-all"
            >
              All Brands
            </Button>
            {brands.map(brand => (
              <Button
                key={brand.id}
                size="sm"
                variant={selectedBrand === brand.id ? "default" : "outline"}
                className={selectedBrand === brand.id 
                  ? "bg-[#10B981] hover:bg-[#059669]" 
                  : "border-white/10 text-gray-400 hover:text-white hover:bg-white/5"}
                onClick={() => { setSelectedBrand(brand.id); setTablePage(1); }}
                data-testid={`brand-filter-${brand.id}`}
              >
                {brand.name}
              </Button>
            ))}
          </div>
        </div>

        {/* Summary KPI Cards */}
        <div className={`grid gap-6 mb-8 ${summary.has_clickable_banners ? 'grid-cols-1 md:grid-cols-3' : 'grid-cols-1 md:grid-cols-2'}`}>
          {/* Total Impressions - Always show */}
          <Card className="bg-[#12121a] border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Total Impressions</p>
                  <p className="text-3xl font-bold text-white" data-testid="total-impressions">
                    {summary.total_impressions.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {summary.total_banners} banner{summary.total_banners !== 1 ? 's' : ''} tracked
                  </p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Eye className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Clicks and CTR - Only show if clickable banners exist */}
          {summary.has_clickable_banners ? (
            <>
              <Card className="bg-[#12121a] border-white/5">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Total Clicks</p>
                      <p className="text-3xl font-bold text-white" data-testid="total-clicks">
                        {summary.total_clicks.toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {summary.clickable_banner_count} clickable banner{summary.clickable_banner_count !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-[#10B981]/10 flex items-center justify-center">
                      <MousePointerClick className="w-6 h-6 text-[#10B981]" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#12121a] border-white/5">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Average CTR</p>
                      <p className="text-3xl font-bold text-white" data-testid="ctr">
                        {summary.ctr.toFixed(2)}%
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        Click-through rate
                      </p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center">
                      <Percent className="w-6 h-6 text-purple-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-[#12121a] border-white/5">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                    <Link2Off className="w-6 h-6 text-amber-500" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-400 font-medium">No Clickable Banners</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Clicks and CTR are only available for banners with a destination URL. 
                      Add a destination URL in the banner editor to enable click tracking.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Email Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="bg-[#12121a] border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Emails Sent</p>
                  <p className="text-3xl font-bold text-white" data-testid="emails-sent">{emailStats.emails_sent.toLocaleString()}</p>
                  <p className="text-xs text-gray-500 mt-1">{emailStats.emails_opened} opened</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-indigo-500/10 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-indigo-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#12121a] border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Open Rate</p>
                  <p className="text-3xl font-bold text-white" data-testid="open-rate">{emailStats.open_rate}%</p>
                  <p className="text-xs text-gray-500 mt-1">{emailStats.emails_opened} of {emailStats.emails_sent} emails</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                  <MailOpen className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#12121a] border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Click Rate</p>
                  <p className="text-3xl font-bold text-white" data-testid="click-rate">{emailStats.click_rate}%</p>
                  <p className="text-xs text-gray-500 mt-1">{emailStats.emails_clicked} of {emailStats.emails_sent} emails</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <MousePointer className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Impressions Over Time - Always shown */}
          <Card className="bg-[#12121a] border-white/5">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-500" />
                Impressions Over Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              {overTime.length === 0 ? (
                <div className="h-64 flex items-center justify-center text-gray-500">
                  No data available for selected date range
                </div>
              ) : (
                <>
                  <div className="h-64 flex items-end gap-1">
                    {overTime.map((day, idx) => {
                      const height = (day.impressions / maxImpressions) * 200;
                      return (
                        <div key={day.date} className="flex-1 flex flex-col items-center group relative">
                          {/* Tooltip */}
                          <div className="absolute bottom-full mb-2 bg-[#1a1a24] border border-white/10 rounded px-2 py-1 text-xs hidden group-hover:block z-10 whitespace-nowrap">
                            <div className="text-gray-400">{format(parseISO(day.date), "MMM d, yyyy")}</div>
                            <div className="text-blue-400">{day.impressions.toLocaleString()} impressions</div>
                          </div>
                          {/* Bar */}
                          <div 
                            className="w-full bg-blue-500/70 rounded-t transition-all hover:bg-blue-500"
                            style={{ height: `${Math.max(height, day.impressions > 0 ? 4 : 0)}px` }}
                          />
                          {overTime.length <= 14 && (
                            <span className="text-xs text-gray-500 rotate-45 origin-left whitespace-nowrap mt-2">
                              {format(parseISO(day.date), "MMM d")}
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {overTime.length > 14 && (
                    <div className="flex justify-between mt-2 text-xs text-gray-500">
                      <span>{format(parseISO(overTime[0]?.date), "MMM d")}</span>
                      <span>{format(parseISO(overTime[Math.floor(overTime.length / 2)]?.date), "MMM d")}</span>
                      <span>{format(parseISO(overTime[overTime.length - 1]?.date), "MMM d")}</span>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Clicks Over Time - Only shown when applicable */}
          {summary.has_clickable_banners ? (
            <Card className="bg-[#12121a] border-white/5">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MousePointerClick className="w-5 h-5 text-[#10B981]" />
                  Clicks Over Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!hasClicksData ? (
                  <div className="h-64 flex items-center justify-center text-gray-500">
                    No clicks recorded in selected date range
                  </div>
                ) : (
                  <>
                    <div className="h-64 flex items-end gap-1">
                      {overTime.map((day, idx) => {
                        const maxClicks = Math.max(...overTime.map(d => d.clicks), 1);
                        const height = (day.clicks / maxClicks) * 200;
                        return (
                          <div key={day.date} className="flex-1 flex flex-col items-center group relative">
                            {/* Tooltip */}
                            <div className="absolute bottom-full mb-2 bg-[#1a1a24] border border-white/10 rounded px-2 py-1 text-xs hidden group-hover:block z-10 whitespace-nowrap">
                              <div className="text-gray-400">{format(parseISO(day.date), "MMM d, yyyy")}</div>
                              <div className="text-[#10B981]">{day.clicks.toLocaleString()} clicks</div>
                            </div>
                            {/* Bar */}
                            <div 
                              className="w-full bg-[#10B981]/70 rounded-t transition-all hover:bg-[#10B981]"
                              style={{ height: `${Math.max(height, day.clicks > 0 ? 4 : 0)}px` }}
                            />
                            {overTime.length <= 14 && (
                              <span className="text-xs text-gray-500 rotate-45 origin-left whitespace-nowrap mt-2">
                                {format(parseISO(day.date), "MMM d")}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {overTime.length > 14 && (
                      <div className="flex justify-between mt-2 text-xs text-gray-500">
                        <span>{format(parseISO(overTime[0]?.date), "MMM d")}</span>
                        <span>{format(parseISO(overTime[Math.floor(overTime.length / 2)]?.date), "MMM d")}</span>
                        <span>{format(parseISO(overTime[overTime.length - 1]?.date), "MMM d")}</span>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card className="bg-[#12121a] border-white/5">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-amber-500" />
                  Banner Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64 flex flex-col items-center justify-center">
                  <div className="text-6xl font-bold text-white mb-2">{summary.total_banners}</div>
                  <p className="text-gray-400">Total Banners</p>
                  <div className="mt-4 flex gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-gray-500"></div>
                      <span className="text-gray-400">{summary.display_only_count} Display-only</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded bg-[#10B981]"></div>
                      <span className="text-gray-400">{summary.clickable_banner_count} Clickable</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Email Engagement Over Time */}
        <Card className="bg-[#12121a] border-white/5 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-indigo-500" />
              Email Engagement Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            {emailOverTime.length === 0 ? (
              <div className="h-64 flex items-center justify-center text-gray-500">
                No email data available for selected date range
              </div>
            ) : (
              <>
                {(() => {
                  const maxVal = Math.max(...emailOverTime.map(d => d.sent), 1);
                  const w = 600;
                  const h = 200;
                  const pad = { top: 10, right: 10, bottom: 30, left: 10 };
                  const cw = w - pad.left - pad.right;
                  const ch = h - pad.top - pad.bottom;
                  const n = emailOverTime.length;
                  const stepX = n > 1 ? cw / (n - 1) : cw / 2;

                  const makePath = (key) => {
                    const pts = emailOverTime.map((d, i) => ({
                      x: pad.left + i * stepX,
                      y: pad.top + ch - (d[key] / maxVal) * ch,
                    }));
                    if (pts.length === 1) return `M${pts[0].x},${pts[0].y}`;
                    let path = `M${pts[0].x},${pts[0].y}`;
                    for (let i = 0; i < pts.length - 1; i++) {
                      const cx = (pts[i].x + pts[i + 1].x) / 2;
                      path += ` C${cx},${pts[i].y} ${cx},${pts[i + 1].y} ${pts[i + 1].x},${pts[i + 1].y}`;
                    }
                    return path;
                  };

                  const makeArea = (key) => {
                    const pts = emailOverTime.map((d, i) => ({
                      x: pad.left + i * stepX,
                      y: pad.top + ch - (d[key] / maxVal) * ch,
                    }));
                    const bottom = pad.top + ch;
                    if (pts.length === 1) return `M${pts[0].x},${bottom} L${pts[0].x},${pts[0].y} L${pts[0].x},${bottom} Z`;
                    let path = `M${pts[0].x},${bottom} L${pts[0].x},${pts[0].y}`;
                    for (let i = 0; i < pts.length - 1; i++) {
                      const cx = (pts[i].x + pts[i + 1].x) / 2;
                      path += ` C${cx},${pts[i].y} ${cx},${pts[i + 1].y} ${pts[i + 1].x},${pts[i + 1].y}`;
                    }
                    path += ` L${pts[pts.length - 1].x},${bottom} Z`;
                    return path;
                  };

                  const lines = [
                    { key: "sent", color: "#818cf8", fill: "rgba(129,140,248,0.1)" },
                    { key: "opened", color: "#10b981", fill: "rgba(16,185,129,0.1)" },
                    { key: "clicked", color: "#3b82f6", fill: "rgba(59,130,246,0.1)" },
                  ];

                  const labelCount = Math.min(emailOverTime.length, 7);
                  const labelStep = Math.max(1, Math.floor((emailOverTime.length - 1) / (labelCount - 1)));

                  return (
                    <div className="relative">
                      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-64" preserveAspectRatio="none">
                        {/* Grid lines */}
                        {[0, 0.25, 0.5, 0.75, 1].map((frac) => (
                          <line key={frac} x1={pad.left} y1={pad.top + ch * (1 - frac)} x2={w - pad.right} y2={pad.top + ch * (1 - frac)} stroke="rgba(255,255,255,0.05)" strokeWidth="0.5" />
                        ))}
                        {/* Area fills */}
                        {lines.map((l) => (
                          <path key={`area-${l.key}`} d={makeArea(l.key)} fill={l.fill} />
                        ))}
                        {/* Lines */}
                        {lines.map((l) => (
                          <path key={`line-${l.key}`} d={makePath(l.key)} fill="none" stroke={l.color} strokeWidth="2" strokeLinecap="round" />
                        ))}
                        {/* Dots */}
                        {lines.map((l) =>
                          emailOverTime.map((d, i) => (
                            <circle key={`dot-${l.key}-${i}`} cx={pad.left + i * stepX} cy={pad.top + ch - (d[l.key] / maxVal) * ch} r="3" fill={l.color} opacity="0.8" />
                          ))
                        )}
                        {/* X axis labels */}
                        {emailOverTime.map((d, i) => {
                          if (i % labelStep !== 0 && i !== emailOverTime.length - 1) return null;
                          return (
                            <text key={`label-${i}`} x={pad.left + i * stepX} y={h - 5} textAnchor="middle" fill="#6b7280" fontSize="9">{format(parseISO(d.date), "MMM d")}</text>
                          );
                        })}
                      </svg>
                      {/* Hover overlay */}
                      <div className="absolute inset-0 flex" style={{ paddingLeft: pad.left, paddingRight: pad.right, paddingTop: pad.top }}>
                        {emailOverTime.map((day, i) => (
                          <div key={day.date} className="flex-1 group relative">
                            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-[#1a1a24] border border-white/10 rounded px-2 py-1 text-xs hidden group-hover:block z-10 whitespace-nowrap">
                              <div className="text-gray-400">{format(parseISO(day.date), "MMM d, yyyy")}</div>
                              <div className="text-indigo-400">{day.sent} sent</div>
                              <div className="text-emerald-400">{day.opened} opened</div>
                              <div className="text-blue-400">{day.clicked} clicked</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                <div className="flex items-center justify-center gap-6 mt-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-indigo-400" />
                    <span className="text-gray-400">Sent</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-emerald-500" />
                    <span className="text-gray-400">Opened</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded bg-blue-500" />
                    <span className="text-gray-400">Clicked</span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Top Banners by Email Engagement */}
        <Card className="bg-[#12121a] border-white/5 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MailOpen className="w-5 h-5 text-emerald-500" />
              Top Banners by Email Engagement
            </CardTitle>
          </CardHeader>
          <CardContent>
            {topBannersEmail.length === 0 ? (
              <div className="py-8 text-center text-gray-500">No email data available for selected date range</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Banner</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Brand</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Sent</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Opened</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Clicked</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Open Rate</th>
                      <th className="text-right py-3 px-4 text-gray-400 font-medium">Click Rate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topBannersEmail.map((b, i) => (
                      <tr key={b.banner_id} className="border-b border-white/5 hover:bg-white/5" data-testid={`top-email-row-${i}`}>
                        <td className="py-3 px-4 text-white font-medium">{b.banner_name}</td>
                        <td className="py-3 px-4 text-purple-300">{b.brand_name || "-"}</td>
                        <td className="py-3 px-4 text-right text-gray-300">{b.sent}</td>
                        <td className="py-3 px-4 text-right text-gray-300">{b.opened}</td>
                        <td className="py-3 px-4 text-right text-gray-300">{b.clicked}</td>
                        <td className="py-3 px-4 text-right">
                          <span className={getRateColor(b.open_rate, 50)}>
                            {b.open_rate}%
                          </span>
                        </td>
                        <td className="py-3 px-4 text-right">
                          <span className={getRateColor(b.click_rate, 20)}>
                            {b.click_rate}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Banner Performance Table */}
        <Card className="bg-[#12121a] border-white/5 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[#10B981]" />
              Banner Performance
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th 
                      className="text-left py-3 px-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                      onClick={() => handleSort("name")}
                    >
                      <div className="flex items-center">
                        Banner
                        {getSortIcon("name")}
                      </div>
                    </th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Type</th>
                    <th 
                      className="text-right py-3 px-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                      onClick={() => handleSort("impressions")}
                    >
                      <div className="flex items-center justify-end">
                        Impressions
                        {getSortIcon("impressions")}
                      </div>
                    </th>
                    <th 
                      className="text-right py-3 px-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                      onClick={() => handleSort("clicks")}
                    >
                      <div className="flex items-center justify-end">
                        Clicks
                        {getSortIcon("clicks")}
                      </div>
                    </th>
                    <th 
                      className="text-right py-3 px-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                      onClick={() => handleSort("ctr")}
                    >
                      <div className="flex items-center justify-end">
                        CTR
                        {getSortIcon("ctr")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {bannerData.data?.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-gray-500">
                        No banners found
                      </td>
                    </tr>
                  ) : (
                    bannerData.data?.map((banner) => (
                      <tr 
                        key={banner.banner_id} 
                        className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                        onClick={() => navigate(`/banner-editor/${banner.banner_id}`)}
                        data-testid={`banner-row-${banner.banner_id}`}
                      >
                        <td className="py-3 px-4">
                          <div>
                            <p className="text-white font-medium">{banner.name}</p>
                            <p className="text-xs text-gray-500">{getLotteryName(banner.lottery_id)}</p>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {banner.is_clickable ? (
                            <span className="inline-flex items-center gap-1 text-[#10B981] text-xs bg-[#10B981]/10 px-2 py-1 rounded">
                              <ExternalLink className="w-3 h-3" />
                              Clickable
                            </span>
                          ) : (
                            <span className="text-gray-500 text-xs bg-white/5 px-2 py-1 rounded">
                              Display-only
                            </span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right text-blue-400 font-medium">
                          {banner.impressions > 0 ? banner.impressions.toLocaleString() : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {banner.is_clickable ? (
                            <span className="text-[#10B981] font-medium">
                              {banner.clicks > 0 ? banner.clicks.toLocaleString() : "-"}
                            </span>
                          ) : (
                            <NAValue reason="Clicks are only tracked for banners with a destination URL configured." />
                          )}
                        </td>
                        <td className="py-3 px-4 text-right">
                          {banner.is_clickable ? (
                            <span className="text-purple-400">
                              {banner.impressions > 0 ? `${banner.ctr.toFixed(2)}%` : "-"}
                            </span>
                          ) : (
                            <NAValue reason="CTR requires click tracking, which is only available for clickable banners." />
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {bannerData.pagination?.total_pages > 1 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                <p className="text-sm text-gray-500">
                  Showing {((tablePage - 1) * tablePageSize) + 1} to {Math.min(tablePage * tablePageSize, bannerData.pagination.total_count)} of {bannerData.pagination.total_count} banners
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-white/10 text-gray-400 hover:text-white"
                    onClick={() => setTablePage(p => Math.max(1, p - 1))}
                    disabled={tablePage === 1}
                    data-testid="prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-400">
                    Page {tablePage} of {bannerData.pagination.total_pages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-white/10 text-gray-400 hover:text-white"
                    onClick={() => setTablePage(p => Math.min(bannerData.pagination.total_pages, p + 1))}
                    disabled={tablePage === bannerData.pagination.total_pages}
                    data-testid="next-page"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Referring Domains */}
        <Card className="bg-[#12121a] border-white/5">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Globe className="w-5 h-5 text-[#10B981]" />
              Top Referring Domains
            </CardTitle>
          </CardHeader>
          <CardContent>
            {byDomain.length === 0 ? (
              <div className="py-8 text-center">
                <Globe className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500">No domain data available yet.</p>
                <p className="text-gray-600 text-sm mt-1">
                  Impressions are tracked when banners are embedded on external websites.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {byDomain.slice(0, 10).map((domain, idx) => {
                  const maxDomainImpressions = Math.max(...byDomain.map(d => d.impressions), 1);
                  const percentage = (domain.impressions / maxDomainImpressions) * 100;
                  
                  return (
                    <div 
                      key={domain.domain || idx} 
                      className="group"
                      data-testid={`domain-${domain.domain || 'direct'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-white truncate max-w-[200px]" title={domain.domain || "Direct / Unknown"}>
                          {domain.domain || "Direct / Unknown"}
                        </span>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-blue-400">
                            {domain.impressions.toLocaleString()} <span className="text-gray-500">impressions</span>
                          </span>
                          {summary.has_clickable_banners && (
                            <>
                              <span className="text-[#10B981]">
                                {domain.clicks.toLocaleString()} <span className="text-gray-500">clicks</span>
                              </span>
                              <span className="text-purple-400 min-w-[60px] text-right">
                                {domain.ctr.toFixed(1)}% <span className="text-gray-500">CTR</span>
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="w-full h-2 bg-white/5 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-blue-500 rounded-full transition-all group-hover:bg-blue-400"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
        </div>
      </main>
    </div>
  );
}
